import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Avatar from "../components/Avatar";
import api from "../lib/api";
import { toast } from "sonner";

const Header = ({ children, testid }) => (
  <h2 className="social-head" data-testid={testid}><span className="social-star">★</span>{children}<span className="social-star">★</span></h2>
);

export default function FriendRequestsPage() {
  const navigate = useNavigate();
  const [inc, setInc] = useState([]);
  const [out, setOut] = useState([]);
  const [replies, setReplies] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/friends/pending").then(({ data }) => { setInc(data.incoming || []); setOut(data.outgoing || []); }).catch(() => {});
    api.get("/notifications").then(({ data }) => setReplies((data.items || []).filter((n) => n.type === "community_reply"))).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // Opening the inbox clears the bell badge.
    api.post("/notifications/read").then(() => window.dispatchEvent(new Event("dial-notify-refresh"))).catch(() => {});
  }, [load]);

  const act = async (path, u, msg) => {
    setBusy(u.id);
    try {
      await api.post(path, { user_id: u.id });
      if (msg) toast.success(msg);
      window.dispatchEvent(new Event("dial-notify-refresh"));
      load();
    } catch { toast.error("Something went wrong."); } finally { setBusy(""); }
  };

  const Row = ({ u, children }) => (
    <div className="flex items-center gap-3 rounded border border-dotted border-[var(--border-subtle)] p-2" data-testid={`request-row-${u.username}`}>
      <Avatar user={u} size={44} rounded="rounded-[3px]" />
      <button onClick={() => navigate(`/profile/${u.username}`)} className="min-w-0 flex-1 text-left">
        <p className="truncate font-bold text-[var(--text-primary)]">{u.name}</p>
        <p className="truncate text-xs text-[var(--text-muted)]">@{u.username}</p>
      </button>
      <div className="flex gap-2">{children}</div>
    </div>
  );

  return (
    <div className="social-shell" data-skin="ancientsocial" data-variant="profile" data-testid="friend-requests-page">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 space-y-5">
        <Link to="/friends" className="social-link text-sm" data-testid="back-to-friends">← Back to Friends</Link>

        <section className="social-panel" data-testid="inbox-replies">
          <Header testid="inbox-replies-head">Replies to Your Threads ({replies.length})</Header>
          <div className="space-y-2 p-4">
            {replies.length === 0 ? <p className="text-sm text-[var(--text-secondary)]">No new replies.</p> :
              replies.map((n) => (
                <button key={n.id} onClick={() => navigate(`/community/film/${n.film_slug}/thread/${n.thread_id}`)}
                  className="flex w-full items-center gap-3 rounded border border-dotted border-[var(--border-subtle)] p-2 text-left hover:border-[var(--accent)]" data-testid={`inbox-reply-${n.id}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-[var(--text-primary)]">{n.from_name} replied</span>
                    <span className="block truncate text-xs text-[var(--text-muted)]">on “{n.thread_title}” · {n.film_title}</span>
                  </span>
                </button>
              ))}
          </div>
        </section>

        <section className="social-panel" data-testid="requests-incoming">
          <Header testid="requests-incoming-head">Incoming Requests ({inc.length})</Header>
          <div className="space-y-2 p-4">
            {inc.length === 0 ? <p className="text-sm text-[var(--text-secondary)]">No incoming requests.</p> :
              inc.map((u) => (
                <Row key={u.id} u={u}>
                  <button disabled={busy === u.id} onClick={() => act("/friends/accept", u, "You're now friends ★")} className="social-pill" data-testid={`request-accept-${u.username}`}>Accept</button>
                  <button disabled={busy === u.id} onClick={() => act("/friends/decline", u, "Declined")} className="social-pill ghost" data-testid={`request-decline-${u.username}`}>Decline</button>
                </Row>
              ))}
          </div>
        </section>

        <section className="social-panel" data-testid="requests-outgoing">
          <Header testid="requests-outgoing-head">Sent Requests ({out.length})</Header>
          <div className="space-y-2 p-4">
            {out.length === 0 ? <p className="text-sm text-[var(--text-secondary)]">No pending sent requests.</p> :
              out.map((u) => (
                <Row key={u.id} u={u}>
                  <button disabled={busy === u.id} onClick={() => act("/friends/cancel", u, "Request cancelled")} className="social-pill ghost" data-testid={`request-cancel-${u.username}`}>Cancel</button>
                </Row>
              ))}
          </div>
        </section>
      </main>
    </div>
  );
}
